import type { EventRecord, Position, PositionAssetClass, Profit, Transaction } from '@fleece/models';
import { Decimal, InvalidRequestError, sumDecimals } from '@fleece/utilities';
import { NA } from '@/lib/format';

/**
 * What the account pages show of the ledger, computed from what it returns.
 *
 * Everything here stays in `Decimal` until it becomes text. The ledger keeps its figures
 * exact so that an account's realised profit is the sum of its transactions to the cent;
 * summing doubles on the way to the screen would show a total that disagrees with the
 * rows above it, which is the one thing a ledger's console must never do.
 */

const MINUS = '−';

/** Thousands separators on the integer part of a plain decimal string, which has no sign. */
function group(unsigned: string): string {
  const [whole, fraction] = unsigned.split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return fraction === undefined ? grouped : `${grouped}.${fraction}`;
}

/**
 * Signed only when what is shown is not zero: −0.004 rounds to "0.00", and a minus in
 * front of it would claim a loss the figure does not show.
 */
function signed(value: Decimal, unsigned: string): string {
  return value.isNegative() && /[1-9]/.test(unsigned) ? `${MINUS}${unsigned}` : unsigned;
}

/** Dollars, signed with a true minus: −1234.5 → "−$1,234.50". */
export function money(value: Decimal | undefined, digits = 2): string {
  if (value === undefined) {
    return NA;
  }
  const unsigned = `$${group(value.abs().toFixed(digits))}`;
  return signed(value, unsigned);
}

/**
 * A size, exactly: every digit the ledger holds, since a fractional share or a crypto
 * size rounded for display is a holding that no longer adds up.
 */
export function quantity(value: Decimal): string {
  return signed(value, group(value.abs().toString()));
}

/**
 * A price as a broker quotes it: cents from a dollar up, and below one up to four places,
 * where cents would round a sub-dollar stock to nothing — but no more than it has, so a
 * contract at 0.25 reads as one does on a chain. The exact figure is the cell's title.
 */
export function quote(value: Decimal): string {
  const unsigned = value.abs();
  if (unsigned.gte(Decimal.ONE) || unsigned.isZero()) {
    return signed(value, group(unsigned.toFixed(2)));
  }
  return signed(value, unsigned.toFixed(4).replace(/(\.\d\d\d*?)0+$/, '$1'));
}

/** A return the ledger states in basis points, as a signed percentage: 1234 → "+12.34%". */
export function roiPercent(basisPoints: Decimal | undefined): string {
  if (basisPoints === undefined) {
    return NA;
  }
  const percent = basisPoints.div(Decimal.of(100), 4);
  const unsigned = `${percent.abs().toFixed(2)}%`;
  if (!/[1-9]/.test(unsigned)) {
    return unsigned;
  }
  return percent.isNegative() ? `${MINUS}${unsigned}` : `+${unsigned}`;
}

/**
 * Which way a figure leans, for colouring it — never the only thing that says so. Judged
 * on the figure as shown to `digits` places, so −0.004, shown as "$0.00", is not coloured
 * as the loss its text deliberately does not claim.
 */
export function tone(value: Decimal | undefined, digits = 2): 'positive' | 'negative' | 'muted' {
  if (value === undefined || !/[1-9]/.test(value.abs().toFixed(digits))) {
    return 'muted';
  }
  return value.isNegative() ? 'negative' : 'positive';
}

export interface OptionContract {
  /**
   * The contract's root as OCC writes it, with the digit that marks an adjusted contract —
   * `1AAPL` — which delivers something other than 100 shares and must not read the same as
   * the standard one.
   */
  readonly root: string;
  readonly expiration: string;
  readonly right: 'call' | 'put';
  /** Without trailing zeros: "500", "97.5". */
  readonly strike: string;
}

/**
 * Same layout as `parseOccSymbol` in `@fleece/marketdata`, which the console cannot import
 * — its dependency is the client, not the data clients. This one only labels a row, so a
 * symbol it does not read is shown as it is rather than refused.
 */
const OCC = /^(\d?)([A-Z]{1,5})(\d?)(\d{2})(\d{2})(\d{2})([CP])(\d{8})$/;

/** An option's contract terms, read from its OCC symbol; undefined for anything else. */
export function optionContract(symbol: string, assetClass: PositionAssetClass): OptionContract | undefined {
  if (assetClass !== 'option') {
    return undefined;
  }
  const match = OCC.exec(symbol);
  if (match === null) {
    return undefined;
  }
  const [, prefix, underlying, suffix, year, month, day, right, strikeMils] = match;
  return {
    root: `${prefix}${underlying}${suffix}`,
    expiration: `20${year}-${month}-${day}`,
    right: right === 'C' ? 'call' : 'put',
    strike: Decimal.of(strikeMils).div(Decimal.of(1000), 3).toString(),
  };
}

/** A symbol on one line: an option's terms written out, cash by name, anything else as it is. */
export function symbolLabel(symbol: string, assetClass: PositionAssetClass): string {
  if (assetClass === 'cash') {
    return 'Cash';
  }
  const contract = optionContract(symbol, assetClass);
  return contract === undefined ? symbol : `${contract.root} ${contract.strike} ${contract.right} ${contract.expiration}`;
}

export type TradeSide = 'buy' | 'sell';

/**
 * What a transaction did to the position it was booked against. Told apart by the size it
 * left behind and the size before it, which is what separates selling to open a short leg
 * from selling to close a long one — the same "sell" either way.
 */
export type TradeEffect = 'open' | 'add' | 'reduce' | 'close' | 'flip';

export interface TradeDescription {
  readonly side: TradeSide;
  readonly effect: TradeEffect;
}

export function describeTrade(transaction: Pick<Transaction, 'size' | 'cumulativeSize'>): TradeDescription {
  const side: TradeSide = transaction.size.isNegative() ? 'sell' : 'buy';
  const after = transaction.cumulativeSize;
  const before = after.sub(transaction.size);
  if (before.isZero()) {
    return { side, effect: 'open' };
  }
  if (after.isZero()) {
    return { side, effect: 'close' };
  }
  if (before.signum() !== after.signum()) {
    return { side, effect: 'flip' };
  }
  return { side, effect: after.abs().gt(before.abs()) ? 'add' : 'reduce' };
}

/** Realised profit to date across every symbol the account has traded, exactly. */
export function realisedTotal(profits: ReadonlyArray<Profit>): Decimal {
  return sumDecimals(profits.map((entry) => entry.profit));
}

export interface PositionRow {
  readonly position: Position;
  /** Undefined until the symbol has realised anything — which an open position often has not. */
  readonly realised?: Decimal;
}

/** Each position beside what its symbol has realised, so a closed one still says what it made. */
export function positionRows(positions: ReadonlyArray<Position>, profits: ReadonlyArray<Profit>): ReadonlyArray<PositionRow> {
  const realised = new Map(profits.map((entry) => [entry.symbol, entry.profit]));
  return positions.map((position) => ({ position, realised: realised.get(position.symbol) }));
}

/** How many transactions one page of the log asks for. */
export const TRANSACTION_PAGE_SIZE = 50;

/** The most rows the service returns for one request; it refuses a larger `limit`. */
export const MAX_TRANSACTION_LIMIT = 1000;

/**
 * Where the next page of a newest-first log starts.
 *
 * `from` is inclusive, so starting at the last timestamp seen returns the rows at that
 * timestamp a second time. Starting a millisecond earlier instead would lose any not yet
 * seen — a spread's legs fill in the same instant, and a page can end between them. So the
 * next page starts at that timestamp and skips the rows already shown there, asking for
 * that many more so that it still makes progress when they fill a page by themselves.
 */
export interface TransactionCursor {
  readonly from: number;
  readonly skip: number;
}

/**
 * The newest end of the log, rather than the time the page was opened: a refresh then asks
 * for the same window and a fill booked since appears on top, and a browser whose clock
 * runs behind the service's still sees the fill that has just been booked.
 */
export const FIRST_TRANSACTION_CURSOR: TransactionCursor = { from: Date.UTC(9999, 11, 31), skip: 0 };

/** Undefined once a page has come back short: the log has no more. */
export function nextTransactionCursor(shown: ReadonlyArray<Transaction>, lastPageFull: boolean): TransactionCursor | undefined {
  const last = shown.at(-1);
  if (!lastPageFull || last === undefined) {
    return undefined;
  }
  let skip = 0;
  for (let index = shown.length - 1; index >= 0 && shown[index].timestamp === last.timestamp; index -= 1) {
    skip += 1;
  }
  return { from: last.timestamp, skip };
}

/**
 * Drops the rows a cursor has already shown. Only from the front, and only at the cursor's
 * timestamp: those are the rows the service returns again, in the same order, and a row
 * anywhere else is new however much it resembles one already on screen.
 */
export function unseenTransactions(page: ReadonlyArray<Transaction>, cursor: TransactionCursor): ReadonlyArray<Transaction> {
  let start = 0;
  while (start < cursor.skip && start < page.length && page[start].timestamp === cursor.from) {
    start += 1;
  }
  return page.slice(start);
}

/**
 * How many rows to ask for at `cursor`: a page, plus the rows already shown that the
 * service will return again — capped at what it allows, which still makes progress for as
 * long as the rows to skip are fewer.
 */
export function transactionLimit(cursor: TransactionCursor): number {
  if (cursor.skip >= MAX_TRANSACTION_LIMIT) {
    // A fact about the data, not a bug: the cursor pages by timestamp, and this many rows
    // at one instant are more than one request may return.
    throw new InvalidRequestError(
      `More than ${MAX_TRANSACTION_LIMIT} transactions share the timestamp ${new Date(cursor.from).toISOString()}, more than the service returns at once, so the log cannot be paged past them. If they are in several symbols, narrow the log to one with a position's filter to read on.`,
    );
  }
  return Math.min(TRANSACTION_PAGE_SIZE + cursor.skip, MAX_TRANSACTION_LIMIT);
}

export interface TransactionPage {
  readonly transactions: ReadonlyArray<Transaction>;
  /** A full answer says there may be more; a short one says the log has ended. */
  readonly full: boolean;
}

/** One page of the log, from what the service answered at `cursor` when asked for `limit` rows. */
export function transactionPage(answered: ReadonlyArray<Transaction>, cursor: TransactionCursor, limit: number): TransactionPage {
  return { transactions: unseenTransactions(answered, cursor), full: answered.length === limit };
}

/**
 * The pages that have rows. Only the last can be empty — the request after a log that
 * ended exactly on a page boundary — and it is not a page to number or show.
 */
export function pagesWithRows(pages: ReadonlyArray<TransactionPage> | undefined): ReadonlyArray<TransactionPage> {
  return (pages ?? []).filter((page) => page.transactions.length > 0);
}

/** A key for a transaction row. One order can fill several times, so its id alone repeats. */
export function transactionKey(transaction: Transaction): string {
  return `${transaction.recordId}:${transaction.symbol}:${transaction.timestamp}:${transaction.cumulativeSize.toString()}`;
}

/**
 * The page numbers a pager shows, 0-based, with `'gap'` where some are left out: always the
 * first and the furthest reached, and the current page with one either side. A gap of a
 * single page shows that page instead, since "…" would be no shorter.
 *
 * Only pages already fetched can be numbered — the log is paged by timestamp and nothing
 * says how long it is — so the last number is the furthest reached, not the end of the log.
 */
export function pageNumbers(count: number, current: number): ReadonlyArray<number | 'gap'> {
  const shown = [...new Set([0, current - 1, current, current + 1, count - 1])].filter((index) => index >= 0 && index < count).sort((a, b) => a - b);
  const items: Array<number | 'gap'> = [];
  shown.forEach((index, position) => {
    const previous = position === 0 ? undefined : shown[position - 1];
    if (previous !== undefined && index - previous === 2) {
      items.push(previous + 1);
    } else if (previous !== undefined && index - previous > 2) {
      items.push('gap');
    }
    items.push(index);
  });
  return items;
}

/**
 * A broker's event, kept as the text it arrived as.
 *
 * Read back as JSON for showing, and nothing else: the payload is the broker's and the
 * ledger never parses it, so one that is not JSON is shown as the text it is.
 */
export function recordPayload(record: EventRecord): unknown {
  try {
    const parsed: unknown = JSON.parse(record.payload);
    return parsed;
  } catch {
    return record.payload;
  }
}

/**
 * A recorded broker event, named by the status it reported — `new`, `partially_filled`,
 * `filled` for an Alpaca order. A payload without one is named by its place instead.
 */
export function orderRecordLabel(record: EventRecord, index: number): string {
  const payload = recordPayload(record);
  if (typeof payload === 'object' && payload !== null && 'status' in payload && typeof payload.status === 'string' && payload.status !== '') {
    return payload.status;
  }
  return `event ${index + 1}`;
}
